import { Theme } from "@mui/material";
import { CHAT_INPUT_SIDE, CHAT_INPUT_STATE, INPUT_TYPES, priorityRoutes, TICKET_PRIORITY, TICKET_STATUS } from "../constants/appConstants";
import { ChatInputSide, ChatInputState, IChatInput, IContentTypeCounts, IUserInfo, ContentSegment } from "../interfaces/AppInterfaces";
import store from "../redux/store";
import { ChatFeedback, getChatFeedback } from "../redux/slices/chatsFeedbackSlice";
import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";
import lazyRoutes from "../routes";

export const formatSecondsToHHMMSS = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const paddedHours = String(hours).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(seconds).padStart(2, '0');

    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
};

export const getPlainTextChatInput = (chatId: string, userQuery: string, side: ChatInputSide): IChatInput => {
    const chatInput: IChatInput = {
        type: INPUT_TYPES.SIMPLE_TEXT,
        chat: {
            chatId: chatId,
            data: [
                {
                    value: userQuery,
                    mentioning: ''
                }
            ],
            date: new Date().toString(),
            heading: 'User Input',
            side: CHAT_INPUT_SIDE.USER as ChatInputSide,
            state: CHAT_INPUT_STATE.NEW as ChatInputState,
            stateText: '',
            subHeading: '',
        }
    }
    return chatInput;
};

export const getPriorityStyles = (theme: Theme, priority: string) => {

    let priorityValue = '';
    if (priority.toLowerCase().indexOf(TICKET_PRIORITY.HIGH.toLowerCase()) !== -1) {
        priorityValue = TICKET_PRIORITY.HIGH;
    } else if (priority.toLowerCase().indexOf(TICKET_PRIORITY.CRITICAL.toLowerCase()) !== -1) {
        priorityValue = TICKET_PRIORITY.CRITICAL;
    }
    else if (priority.toLowerCase().indexOf(TICKET_PRIORITY.LOW.toLowerCase()) !== -1) {
        priorityValue = TICKET_PRIORITY.LOW;
    }
    else if (priority.toLowerCase().indexOf(TICKET_PRIORITY.MEDIUM.toLowerCase()) !== -1) {
        priorityValue = TICKET_PRIORITY.MEDIUM;
    }
    else if (priority.toLowerCase().indexOf(TICKET_PRIORITY.MODERATE.toLowerCase()) !== -1) {
        priorityValue = TICKET_PRIORITY.MODERATE;
    }
    else if (priority.toLowerCase().indexOf(TICKET_PRIORITY.PLANNING.toLowerCase()) !== -1) {
        priorityValue = TICKET_PRIORITY.PLANNING;
    }
    switch (priorityValue) {
        case TICKET_PRIORITY.HIGH:
            return { border: '1px solid', backgroundColor: theme.palette.priority.high, borderColor: theme.palette.priorityBorder.high, height: '20px', borderRadius: '4px', color: theme.palette.priorityBorder.high, fontWeight: 'bold', fontSize: '0.7rem', textTransform: 'uppercase' };
        case TICKET_PRIORITY.CRITICAL:
            return { border: '1px solid', backgroundColor: theme.palette.priority.critical, borderColor: theme.palette.priorityBorder.critical, height: '20px', borderRadius: '4px', color: theme.palette.priorityBorder.critical, fontWeight: 'bold', fontSize: '0.7rem', textTransform: 'uppercase' };
        case TICKET_PRIORITY.LOW:
            return { border: '1px solid', backgroundColor: theme.palette.priority.low, borderColor: theme.palette.priorityBorder.low, height: '20px', borderRadius: '4px', color: theme.palette.priorityBorder.low, fontWeight: 'bold', fontSize: '0.7rem', textTransform: 'uppercase' };
        case TICKET_PRIORITY.MEDIUM:
        case TICKET_PRIORITY.MODERATE:
            return { border: '1px solid', backgroundColor: theme.palette.priority.medium, borderColor: theme.palette.priorityBorder.medium, height: '20px', borderRadius: '4px', color: theme.palette.priorityBorder.medium, fontWeight: 'bold', fontSize: '0.7rem', textTransform: 'uppercase' };
        default:
            return { border: '1px solid', backgroundColor: theme.palette.background.default, borderColor: theme.palette.customBorder.all, height: '20px', borderRadius: '4px', color: theme.palette.text.primary, fontWeight: 'bold', fontSize: '0.7rem', textTransform: 'uppercase' };
    }
};

export const getTicketStatus = (theme: Theme, status: string) => {
    switch (status) {
        case TICKET_STATUS.ASSIGNED:
            return { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, fontWeight: 'bold', fontSize: '0.7rem', borderRadius: '4px', height: '20px', textTransform: 'uppercase' }
        default:
            return { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, fontWeight: 'bold', fontSize: '0.7rem', borderRadius: '4px', height: '20px', textTransform: 'uppercase' }
    }
}

export const getTextfieldCommonStyles = (theme: Theme) => {
    return {
        backgroundColor: theme.palette.background.paper,
        borderWidth: '2px',
        borderColor: theme.palette.text.primary,
        borderRadius: '4px',
        height: '40px',
        maxHeight: '40px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.customBorder.all
        },
        '& .MuiOutlinedInput-root': {
            height: '40px',
        }
    }
};

export const getUserDataFromJWTToken = (token: any) => {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const userdata = JSON.parse(jsonPayload);
    return userdata;
}

export const convertToIST = (exp: number): string => {
  if (!exp || typeof exp !== "number") {
    throw new Error("Invalid exp value");
  }

  return new Date(exp * 1000).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const getUserInformation = (token: any): IUserInfo => {
    // const userDataAllFieldsFromToken = {
    //     "aud": "9004ce3f-f38d-449e-93e0-26d9a8d4167b",
    //     "iss": "https://login.microsoftonline.com/3975b93d-22b8-4c41-8925-83c27440739b/v2.0",
    //     "iat": 1751450954,
    //     "nbf": 1751450954,
    //     "exp": 1751454854,
    //     "aio": "AcQAO/8ZAAAAZ0pKMkkoqG6yeAYjHnXEPAiTha3lDR9WaYy9TmrVzudx/cO00CPIC9BoVlAVpvAJkofhYT/yEFGwxAMPqknRvs6iM7o/ghbXEjVPvYSHpJYVOIm73J37GcLgTsrh55EV5tTrg3OjC1yBO7KNJYYpvmi5tBXttsBsC62IdUQPN/oMv3RA37C9K42gkPIKK+VQHejXy+01DLHkAa95qZ58Fv9TYTG105a06S/ysR2H8iR+NsdsfKTeEN+Bu2oStOoG",
    //     "email": "jayesh.sharma1@wipro.com",
    //     "idp": "https://sts.windows.net/258ac4e4-146a-411e-9dc8-79a9e12fd6da/",
    //     "name": "Jayesh Sharma",
    //     "nonce": "0197caa1-2c2c-7d91-a4e6-2ea671db29bf",
    //     "oid": "73f33365-bc7d-49cc-902e-38f71c76be43",
    //     "preferred_username": "JA269658@wipro.com",
    //     "rh": "1.Ab0APbl1ObgiQUyJJYPCdEBzmz_OBJCN855Ek-Am2ajUFnvLABu9AA.",
    //     "sid": "005f17d9-47c2-0c99-ee22-6cd931c252cf",
    //     "sub": "eVHGzTzKSS0AbK5vV0FL9by7dmOHwmfiC3Jgdir_h1c",
    //     "tid": "3975b93d-22b8-4c41-8925-83c27440739b",
    //     "uti": "ckOwSl8SZ0eLUhQYge-9AA",
    //     "ver": "2.0"
    // }
    const userData = getUserDataFromJWTToken(token);
    return {
        name: userData.name,
        email: userData.email,
        preferredName: userData.preferred_username,
        roles: userData.roles,
        expires_in: new Date(userData.exp * 1000).toISOString(), // canonical, UTC ISO
        issued_at: new Date(userData.iat * 1000).toISOString(),
    }
}

export const showTicketManagementOptions = (ticketStatus: string): boolean => {
    return (ticketStatus === TICKET_STATUS.NEW || ticketStatus == TICKET_STATUS.IN_PROGRESS);
}

const getTextContent = (textContent: any, item: any, chatId: string) => {
    const toolbarInput = {
        cid: chatId,
        messageId: item.message_id,
        like: item.feedback.like,
        dislike: item.feedback.dislike,
        comment: item.feedback.feedbackComments,
        response: {
            response: textContent
        }
    }
    const updatedTextContent = `<div style = "display: flex; align-items: flex-start; flex-direction: column" >
                        <div style="margin-top: 10px; text-align: left; margin-right: 5px; flex: 1;">${textContent}</div>
                        <chat-feedback-toolbar style="width: 100%" class="chatFeedback" datainput='${JSON.stringify(toolbarInput)} '></chat-feedback-toolbar>
            </div>`
    return updatedTextContent;
}

export const getUpdatedChatHistory = (chatHistory: any, chatId: string): any => {
    const updatedChatHistory = chatHistory.map((item: any) => {
        let validJson = item.message.replace(/True/g, 'true').replace(/False/g, 'false').replace(/'/g, '"');
        if (item.role === "Agent") {
            let additionalInfo = "";
            // Use JSON.parse to convert the string to a JSON object
            let textContent;
            try {
                let jsonObject = JSON.parse(validJson);
                const additionalInfo1 = `Created on: ${jsonObject?.results?.ticketing?.retrieve?.ticket_data.sys_created_on}`;
                const additionalInfo2 = `Description: ${jsonObject.results?.ticketing?.retrieve?.ticket_data.description}`;
                textContent = `${JSON.stringify(jsonObject.results.ticketing.create?.response || jsonObject.results.ticketing.retrieve?.response || 'No result found')}${additionalInfo1 ? `\n${additionalInfo1}` : ''}${additionalInfo2 ? `\n${additionalInfo2}` : ''}`
                //const updatedTextContent = getTextContent(textContent, item.message_id);
                //textContent = updatedTextContent;
            } catch (e: any) {
                textContent = validJson;
                //textContent = getTextContent(textContent, item.message_id);
            }
            const processedContent = renderMarkdownWithHtml(textContent);
            return {
                ...item,
                role: "ai",
                html: processedContent 
            };
        } else if (item.role === "User") {
            return {
                ...item,
                role: "user",
                text: getChatText(item.message)
            };
        }
        return item;
    });
    return updatedChatHistory;
}


const getChatText = (value: any): string => {
    let chatText;
    try {
        chatText = JSON.stringify(value);
    } catch (e: any) {
        chatText = value;
    }

    return chatText.replace(/['"]/g, '');
};

export const renderMarkdownWithHtml = (text: string) => {
    const md = new MarkdownIt({
        html: true,
        linkify: true,
        breaks: true,
        typographer: true,
    });

    // Enabling table plugin explicitly
    md.enable(['table']);
    let rawHtml = md.render(text);

    // Add inline styles to table elements and links in single pass
    const styleMap: Record<string, string> = {
        '<table>': '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; font-size: 0.875rem; border: 1px solid #e0e0e0;">',
        '<thead>': '<thead style="background-color: #f5f5f5;">',
        '<th>': '<th style="padding: 10px 12px; text-align: left; font-weight: 600; border: 1px solid #e0e0e0; background-color: #f5f5f5;">',
        '<td>': '<td style="padding: 10px 12px; border: 1px solid #e0e0e0;">'
    };

    rawHtml = rawHtml.replace(/<table>|<thead>|<th>|<td>/gi, (match) => styleMap[match.toLowerCase()]);

    // regex to cover more <a> tag variations
    rawHtml = rawHtml.replace(
        /<a\b([^>]*?)href="([^"]+)"(.*?)>/gi,
        `<a$1 href="$2"$3 target="_blank" rel="noopener noreferrer" onclick="window.open('$2', '_blank')">`
    );

    const cleanHtml = DOMPurify.sanitize(rawHtml);
    return cleanHtml;
};

const HTML_DOC_START = /(?=<!doctype\s+html|<html[\s>])/gi;
const HTML_DOC_END = /<\/html\s*>/gi;

// Collects code fence ranges once for the full text, used to skip false positives
const getCodeFenceRanges = (text: string): Array<[number, number]> => {
    const ranges: Array<[number, number]> = [];
    const pattern = /```[\s\S]*?```/g;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        ranges.push([match.index, match.index + match[0].length]);
    }
    return ranges;
};

const isInsideCodeFence = (index: number, ranges: Array<[number, number]>): boolean =>
    ranges.some(([s, e]) => index >= s && index < e);

/**
 * @deprecated Used for legacy text-based HTML detection. Prefer type-field based detection.
 * 
 * Validates that an HTML start match is real: not in a code fence, and has structural tags for bare <html>
 */
const isVerifiedHtmlStart = (text: string, index: number, fenceRanges: Array<[number, number]>): boolean => {
    if (isInsideCodeFence(index, fenceRanges)) return false;
    const candidate = text.slice(index).toLowerCase();
    if (candidate.startsWith('<!doctype')) return true;
    if (candidate.startsWith('<html')) {
        const lookAhead = candidate.slice(0, 2000);
        return lookAhead.includes('<head') || lookAhead.includes('<body') || lookAhead.includes('<style');
    }
    return false;
};

/**
 * @deprecated Used for legacy text-based HTML detection. Prefer type-field based detection
 * with `event.type === 'html'` in streaming responses.
 * 
 * Used by streaming to detect when HTML starts appearing in accumulated text.
 */
export const findHtmlBoundary = (text: string): number => {
    const fenceRanges = getCodeFenceRanges(text);
    const pattern = new RegExp(HTML_DOC_START.source, 'gi');
    let match;
    while ((match = pattern.exec(text)) !== null) {
        if (isVerifiedHtmlStart(text, match.index, fenceRanges)) return match.index;
    }
    return -1;
};

/**
 * @deprecated Used for legacy text-based HTML detection. Prefer type-field based detection
 * with `event.type === 'html'` in streaming responses.
 */
export const containsHtmlDocument = (text: string): boolean => {
    const lower = text.toLowerCase();
    if (!lower.includes('<!doctype') && !lower.includes('<html')) return false;
    return findHtmlBoundary(text) !== -1;
};

// Splits text into alternating 'text' and 'html' segments, computing code fences once
const splitContentSegments = (text: string): ContentSegment[] => {
    const lower = text.toLowerCase();
    if (!lower.includes('<!doctype') && !lower.includes('<html')) {
        return [{ type: 'text', content: text }];
    }
    const segments: ContentSegment[] = [];
    const fenceRanges = getCodeFenceRanges(text);
    const startPattern = new RegExp(HTML_DOC_START.source, 'gi');
    let cursor = 0;

    while (cursor < text.length) {
        startPattern.lastIndex = cursor;
        let startIndex = -1;
        let match;
        while ((match = startPattern.exec(text)) !== null) {
            if (isVerifiedHtmlStart(text, match.index, fenceRanges)) {
                startIndex = match.index;
                break;
            }
        }

        if (startIndex === -1) {
            segments.push({ type: 'text', content: text.slice(cursor) });
            break;
        }

        if (startIndex > cursor) {
            segments.push({ type: 'text', content: text.slice(cursor, startIndex) });
        }

        const endPattern = new RegExp(HTML_DOC_END.source, 'gi');
        endPattern.lastIndex = startIndex;
        let endIndex = -1;
        let endMatch;
        while ((endMatch = endPattern.exec(text)) !== null) {
            if (!isInsideCodeFence(endMatch.index, fenceRanges)) {
                endIndex = endMatch.index + endMatch[0].length;
                break;
            }
        }

        if (endIndex !== -1) {
            segments.push({ type: 'html', content: text.slice(startIndex, endIndex) });
            cursor = endIndex;
        } else {
            segments.push({ type: 'html', content: text.slice(startIndex) });
            break;
        }
    }

    return segments.filter(s => s.content.trim().length > 0);
};

// Renders HTML inside a sandboxed iframe with auto-resize via postMessage
const renderFullHtmlResponse = (html: string): string => {
    const uniqueId = `html-iframe-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
    const modalId = `html-modal-${uniqueId}`;
    const resizeScript = `<script>
        function notifyHeight(){var h=document.documentElement.scrollHeight;window.parent.postMessage({type:'iframe-resize',id:'${uniqueId}',height:h},'*')}
        window.addEventListener('load',notifyHeight);
        new MutationObserver(notifyHeight).observe(document.body,{childList:true,subtree:true});
        setTimeout(notifyHeight,100);setTimeout(notifyHeight,500);
    </script>`;

    const enhancedHtml = html.toLowerCase().includes('</body>')
        ? html.replace(/<\/body>/i, `${resizeScript}</body>`)
        : html + resizeScript;

    const encodedHtml = enhancedHtml.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    const base64Html = btoa(unescape(encodeURIComponent(html)));

    return `<div style="width:min(750px,85vw);min-width:min(750px,85vw);position:relative;" class="html-response-container">
        <button class="html-popup-btn" title="Expand" data-html="${base64Html}" data-modal="${modalId}" onclick="(function(btn){
            var html=decodeURIComponent(escape(atob(btn.dataset.html)));
            var modalId=btn.dataset.modal;
            var overlay=document.createElement('div');
            overlay.id=modalId;
            overlay.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.6);z-index:9999;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(2px)';
            var modal=document.createElement('div');
            modal.style.cssText='background:#fff;border-radius:12px;width:90vw;max-width:1100px;height:85vh;max-height:900px;position:relative;box-shadow:0 25px 50px rgba(0,0,0,0.25);overflow:hidden;animation:modalFadeIn 0.2s ease';
            var closeBtn=document.createElement('button');
            closeBtn.innerHTML='&times;';
            closeBtn.style.cssText='position:absolute;top:12px;right:12px;width:32px;height:32px;border:none;background:#f3f4f6;color:#666;font-size:20px;border-radius:50%;cursor:pointer;z-index:10;display:flex;align-items:center;justify-content:center;transition:all 0.2s';
            closeBtn.onmouseover=function(){this.style.background='#e5e7eb';this.style.color='#333'};
            closeBtn.onmouseout=function(){this.style.background='#f3f4f6';this.style.color='#666'};
            closeBtn.onclick=function(){overlay.remove()};
            var iframe=document.createElement('iframe');
            iframe.srcdoc=html;
            iframe.sandbox='allow-scripts';
            iframe.style.cssText='width:100%;height:100%;border:none';
            modal.appendChild(closeBtn);
            modal.appendChild(iframe);
            overlay.appendChild(modal);
            overlay.onclick=function(e){if(e.target===overlay)overlay.remove()};
            document.body.appendChild(overlay);
        })(this)" style="position:absolute;top:-20px;right:-17px;z-index:10;background:transparent;border:none;padding:4px;cursor:pointer;opacity:0.5;transition:opacity 0.2s;" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.5'">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
        </button>
        <iframe srcdoc="${encodedHtml}" sandbox="allow-scripts"
            style="width:100%;border:none;overflow:hidden;min-height:200px;"
            onload="var s=this;window.addEventListener('message',function(e){if(e.data&&e.data.type==='iframe-resize'&&e.data.id==='${uniqueId}')s.style.height=e.data.height+'px'})">
        </iframe></div>`;
};

// Entry point: renders markdown as-is, or splits mixed content and renders each segment appropriately
export const smartRenderHtml = (text: string): string => {
    const segments = splitContentSegments(text);
    if (segments.length === 1 && segments[0].type === 'text') {
        return renderMarkdownWithHtml(text);
    }
    return segments.map(s => s.type === 'html' ? renderFullHtmlResponse(s.content) : renderMarkdownWithHtml(s.content)).join('');
};

/**
 * Renders an array of ResponseSegments (from type-field based streaming) into HTML.
 * Each segment is either 'token' (markdown) or 'html' (complete HTML document).
 * 
 * @param segments - Array of ResponseSegment objects from streaming
 * @returns Combined HTML string with each segment rendered appropriately
 */
export const renderSegments = (segments: Array<{ type: 'token' | 'html'; content: string }>): string => {
    if (!segments.length) return '';
    
    return segments.map(segment => {
        if (segment.type === 'html') {
            return renderFullHtmlResponse(segment.content);
        }
        return renderMarkdownWithHtml(segment.content);
    }).join('');
};

function getLazyComponentByUserRole(userRole: any) {
    const userPriorityRoute = userRole?.includes('end_user')
        ? priorityRoutes['end_user']
        : priorityRoutes[userRole[0]];
    const matchedRoute = lazyRoutes.find((route) => route.path === userPriorityRoute);
    return matchedRoute;
};

export const summarizeGroup = (
    types: Record<string, string>,
    allCounts: IContentTypeCounts
): { count: number; labels: string[] } => {
    let count = 0;
    const labels: string[] = [];

    for (const [mime, label] of Object.entries(types)) {
        const found = allCounts[mime];
        if (found) {
            count += found;
            labels.push(label);
        }
    }

    return { count, labels };
};

export function prettify(label: string) {
    return label.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export const filterEmptyValues = (obj: Record<string, any>): Record<string, any> => {
    const filtered: Record<string, any> = {};
    
    Object.entries(obj).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            return;
        }
        
        if (Array.isArray(value)) {
            const filteredArray = value.filter(item => item !== null && item !== undefined && item !== '');
            if (filteredArray.length > 0) {
                filtered[key] = filteredArray;
            }
            return;
        }
        
        if (typeof value === 'object') {
            const filteredObject = filterEmptyValues(value);
            if (Object.keys(filteredObject).length > 0) {
                filtered[key] = filteredObject;
            }
            return;
        }
        filtered[key] = value;
    });
    
    return filtered;
};